import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingRecovery, setIsCheckingRecovery] = useState(true);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const [resetLinkError, setResetLinkError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const establishRecoverySession = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const recoveryCode = searchParams.get('code');
      const hasLegacyRecoveryToken =
        hashParams.get('type') === 'recovery' || hashParams.has('access_token');

      try {
        if (recoveryCode) {
          const { error } = await supabase.auth.exchangeCodeForSession(recoveryCode);
          if (error) throw error;

          if (isMounted) {
            setHasRecoverySession(true);
            setResetLinkError(null);
            window.history.replaceState(null, document.title, window.location.pathname);
          }
          return;
        }

        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (isMounted) {
          setHasRecoverySession(Boolean(data.session || hasLegacyRecoveryToken));
          setResetLinkError(null);
        }
      } catch (error) {
        if (isMounted) {
          setHasRecoverySession(false);
          setResetLinkError(
            error instanceof Error
              ? error.message
              : 'This reset link could not be verified. Please request a new one.',
          );
        }
      } finally {
        if (isMounted) {
          setIsCheckingRecovery(false);
        }
      }
    };

    void establishRecoverySession();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setIsLoading(false);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Password updated. Please sign in.');
      await supabase.auth.signOut();
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Reset Password</CardTitle>
          <CardDescription>Enter a new password for your account</CardDescription>
        </CardHeader>
        <CardContent>
          {isCheckingRecovery ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
              <p className="text-sm text-muted-foreground">Checking your reset link...</p>
            </div>
          ) : !hasRecoverySession ? (
            <div className="space-y-4">
              {resetLinkError && (
                <p className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
                  {resetLinkError}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                This page must be opened from the password reset email link. If you arrived here directly,
                please request a new reset email from the sign-in page.
              </p>
              <Button className="w-full" onClick={() => navigate('/auth')}>
                Back to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
